import React, { useState, useEffect } from 'react';
import { useLocation, Route, Routes } from 'react-router-dom';
import './App.css';
import TurnSelectionStep from './components/TurnSelectionStep/TurnSelectionStep';
import ExtraSelectionStep from './components/ExtraSelectionStep/ExtraSelectionStep';
import SummaryStep from './components/SummaryStep/SummaryStep';
import PersonalDataStep from './components/PersonalDataStep/PersonalDataStep';
import Header from './components/Header/Header';
import SiteTopBar from './components/SiteTopBar/SiteTopBar';
import BookingStepIntro from './components/BookingStepIntro/BookingStepIntro';
import BookingBottomBar from './components/BookingBottomBar/BookingBottomBar';
import BookingSelectionPanel from './components/BookingSelectionPanel/BookingSelectionPanel';
import ShopSelectionStep from './components/ShopSelectionStep/ShopSelectionStep';
import RoomSelectionStep from './components/RoomSelectionStep/RoomSelectionStep';
import ModalitySelectionStep from './components/ModalitySelectionStep/ModalitySelectionStep';
import PagoOk from './components/PagoOk/PagoOk';
import PagoErr from './components/PagoErr/PagoErr';
import { Turn, Extra, Checkout, Room, Shop, ApiResponse, Modality } from './models/types';
import { isDateBookable } from './utils/bookableDate';
import { BOOKING_STEPS, getNextStep, getPreviousStep, isStepNavigationAllowed, LandingMode, parseLandingFromPath } from './constants/bookingSteps';
import { get, post } from './services/api';
import authenticate from './utils/authenticate';
import { buildCheckoutExtras } from './utils/villalbaPromotions';
import { isPersonalDataFormValid } from './utils/personalDataValidation';
import { calculateExtraTotal } from './utils/extraPricing';
import { useIsMobile } from './hooks/useIsMobile';
import { config } from "./config/environment";

function App() {
    const location = useLocation();
    const [shopName, setShopName] = useState('Default Shop');
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [rooms, setRooms] = useState<Room[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
    const [currentStep, setCurrentStep] = useState<number>(BOOKING_STEPS.SHOP);
    const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([BOOKING_STEPS.SHOP]));
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTurn, setSelectedTurn] = useState<Turn | null>(null);
    const [selectedExtras, setSelectedExtras] = useState<Extra[]>([]);
    const [availableExtras, setAvailableExtras] = useState<Extra[]>([]);
    const [showCalendar, setShowCalendar] = useState(true);
    const [customerInfo, setCustomerInfo] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
        postcode: '',
        gettoknow: '',
        additionalInfo: ''
    });
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [marketingAccepted, setMarketingAccepted] = useState(false);
    const [shops, setShops] = useState<Shop[]>([]);
    const [selectedModality, setSelectedModality] = useState<Modality | null>(null);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [landingMode, setLandingMode] = useState<LandingMode>('free');

    const hasMultipleRooms = rooms.length > 1;
    const showRoomInStepper = hasMultipleRooms
        || landingMode === 'shop_room_locked'
        || visitedSteps.has(BOOKING_STEPS.ROOM);
    const isMobile = useIsMobile();
    const includeSummaryStep = isMobile;

    const buildUrlVisitedSteps = (
        step: number,
        mode: LandingMode,
        includeRoomStep: boolean
    ): Set<number> => {
        const steps = new Set<number>([BOOKING_STEPS.SHOP]);
        if (includeRoomStep) {
            steps.add(BOOKING_STEPS.ROOM);
        }
        if (step >= BOOKING_STEPS.TURN) {
            steps.add(BOOKING_STEPS.TURN);
        }
        if (mode === 'free' && step === BOOKING_STEPS.SHOP) {
            return new Set([BOOKING_STEPS.SHOP]);
        }
        return steps;
    };

    const goToStep = (step: number) => {
        setCurrentStep(step);
        if (step === BOOKING_STEPS.TURN) {
            setShowCalendar(true);
        } else if (step >= BOOKING_STEPS.MODALITY) {
            setShowCalendar(false);
        }
    };

    useEffect(() => {
        const measureStickyChrome = () => {
            const topBar = document.querySelector('.site-top-bar');
            const stepper = document.querySelector('.header');
            const intro = document.querySelector('.booking-step-intro');
            const root = document.documentElement;

            // Do not overwrite --site-top-bar-height (controls the bar's own layout).
            // Sticky clearance uses --site-top-bar-offset from SiteTopBar.
            const topBarHeight = topBar instanceof HTMLElement
                ? Math.ceil(topBar.getBoundingClientRect().height)
                : 0;
            const introHeight = intro instanceof HTMLElement ? intro.offsetHeight : 0;
            const headerHeight = Number.parseFloat(
                getComputedStyle(root).getPropertyValue('--booking-header-height')
            ) || (stepper instanceof HTMLElement
                ? Math.ceil(stepper.getBoundingClientRect().height)
                : 0);

            root.style.setProperty('--booking-stepper-height', `${headerHeight}px`);
            root.style.setProperty('--booking-intro-height', `${introHeight}px`);
            root.style.setProperty(
                '--booking-sticky-chrome',
                `${topBarHeight + headerHeight}px`
            );
        };

        const resetScrollToTop = () => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;

            const panelBody = document.querySelector(
                currentStep === BOOKING_STEPS.SUMMARY
                    ? '.summary-step .booking-selection-body'
                    : '.booking-selection-body'
            );
            if (panelBody instanceof HTMLElement) {
                panelBody.scrollTop = 0;
            }
        };

        const syncStickyChromeAndScroll = () => {
            measureStickyChrome();
            resetScrollToTop();

            if (currentStep === BOOKING_STEPS.SUMMARY) {
                return;
            }

            const stepper = document.querySelector('.header');
            const intro = document.querySelector('.booking-step-intro');

            if (intro instanceof HTMLElement && stepper instanceof HTMLElement) {
                const gap = 24;
                const stickyBottom = stepper.getBoundingClientRect().bottom;
                const introTop = intro.getBoundingClientRect().top;
                const delta = introTop - stickyBottom - gap;
                if (Math.abs(delta) > 1) {
                    window.scrollBy({ top: delta, left: 0, behavior: 'auto' });
                }
            }
        };

        resetScrollToTop();

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                syncStickyChromeAndScroll();
                if (currentStep === BOOKING_STEPS.SUMMARY) {
                    requestAnimationFrame(() => {
                        measureStickyChrome();
                        resetScrollToTop();
                    });
                }
            });
        });

        window.addEventListener('resize', measureStickyChrome);
        return () => window.removeEventListener('resize', measureStickyChrome);
    }, [currentStep]);

    useEffect(() => {
        // Keep desktop Resumen below the sticky stepper when the panel opens/resizes.
        const headerHeight = Number.parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue('--booking-header-height')
        );
        if (headerHeight) {
            document.documentElement.style.setProperty(
                '--booking-stepper-height',
                `${headerHeight}px`
            );
        }
    }, [isSummaryOpen, currentStep]);

    useEffect(() => {
        const fetchShopsAndRooms = async () => {
            try {
                const token = await authenticate();
                if (!token) {
                    alert('Failed to authenticate');
                    return;
                }

                const url = `${config.API_BASE_URL}/clientapi/buy/step0`;
                await get(url, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                })
                .then((response: ApiResponse) => {
                    const data = response.message;

                    const shopsWithNumbers = data.map((shop: Shop) => ({
                        ...shop,
                        shop_id: Number(shop.shop_id),
                        rooms: shop.rooms.map(room => ({
                            ...room,
                            room_id: Number(room.room_id),
                            room_num: Number(room.room_num)
                        }))
                    }));

                    setShops(shopsWithNumbers);
                    if (shopsWithNumbers.length > 0) {
                        const landing = parseLandingFromPath(location.pathname);
                        setLandingMode(landing.mode);

                        if (landing.shopId) {
                            const foundShop = shopsWithNumbers.find((shop: Shop) => shop.shop_id === landing.shopId);
                            if (foundShop) {
                                setSelectedShop(foundShop);
                                setShopName(foundShop.shop_name);
                                setRooms(foundShop.rooms);
                                const shopHasMultipleRooms = foundShop.rooms.length > 1;

                                if (landing.roomNum !== null) {
                                    const foundRoom = foundShop.rooms.find((room: Room) => room.room_num === landing.roomNum);
                                    if (foundRoom) {
                                        setSelectedRoom(foundRoom);
                                        setCurrentStep(BOOKING_STEPS.TURN);
                                        setVisitedSteps(buildUrlVisitedSteps(
                                            BOOKING_STEPS.TURN,
                                            landing.mode,
                                            true
                                        ));
                                    } else {
                                        setSelectedRoom(foundShop.rooms[0]);
                                        setCurrentStep(BOOKING_STEPS.ROOM);
                                        setVisitedSteps(buildUrlVisitedSteps(
                                            BOOKING_STEPS.ROOM,
                                            landing.mode,
                                            shopHasMultipleRooms
                                        ));
                                    }
                                } else if (shopHasMultipleRooms) {
                                    setSelectedRoom(null);
                                    setCurrentStep(BOOKING_STEPS.ROOM);
                                    setVisitedSteps(buildUrlVisitedSteps(
                                        BOOKING_STEPS.ROOM,
                                        landing.mode,
                                        true
                                    ));
                                } else {
                                    setSelectedRoom(foundShop.rooms[0]);
                                    setCurrentStep(BOOKING_STEPS.TURN);
                                    setVisitedSteps(buildUrlVisitedSteps(
                                        BOOKING_STEPS.TURN,
                                        landing.mode,
                                        false
                                    ));
                                }
                            }
                        } else {
                            setLandingMode('free');
                            setCurrentStep(BOOKING_STEPS.SHOP);
                            setVisitedSteps(new Set([BOOKING_STEPS.SHOP]));
                        }
                    }
                });
            } catch (error) {
                console.error('Error fetching shops and rooms:', error);
            }
        };

        fetchShopsAndRooms();
    }, [location.pathname]);

    const handleShopSelect = (shop: Shop) => {
        const shopChanged = selectedShop?.shop_id !== shop.shop_id;

        setSelectedShop(shop);
        setShopName(shop.shop_name);
        setRooms(shop.rooms);

        if (shopChanged) {
            setSelectedRoom(shop.rooms.length === 1 ? shop.rooms[0] : null);
            setSelectedDate(null);
            setSelectedTurn(null);
            setSelectedModality(null);
            setSelectedExtras([]);
            setAvailableExtras([]);
        }

        if (shop.rooms.length === 1) {
            setSelectedRoom(shop.rooms[0]);
            setCurrentStep(BOOKING_STEPS.TURN);
            setShowCalendar(true);
            setVisitedSteps(prev => {
                const steps = new Set(prev);
                steps.add(BOOKING_STEPS.SHOP);
                steps.add(BOOKING_STEPS.TURN);
                return steps;
            });
        } else {
            if (shopChanged) {
                setSelectedRoom(null);
            }
            setCurrentStep(BOOKING_STEPS.ROOM);
            setVisitedSteps(prev => {
                const steps = new Set(prev);
                steps.add(BOOKING_STEPS.SHOP);
                steps.add(BOOKING_STEPS.ROOM);
                return steps;
            });
        }
    };

    const handleExtrasSelect = (extras: Extra[]) => {
        setSelectedExtras(extras);
    };

    const handleRemoveExtra = (extraId: number) => {
        setSelectedExtras((prev) => prev.filter((extra) => extra.extra_id !== extraId));
    };

    const handleAvailableExtrasChange = (extras: Extra[]) => {
        setAvailableExtras(extras);
    };

    const handleRoomSelect = (room: Room) => {
        setSelectedRoom(room);
        setCurrentStep(BOOKING_STEPS.TURN);
        setShowCalendar(true);
        setVisitedSteps(prev => {
            const steps = new Set(prev);
            steps.add(BOOKING_STEPS.SHOP);
            steps.add(BOOKING_STEPS.ROOM);
            steps.add(BOOKING_STEPS.TURN);
            return steps;
        });
    };

    const handleModalitySelect = (modality: Modality) => {
        const modalityChanged = selectedModality?.modalityId !== modality.modalityId;

        setSelectedModality(modality);

        // Extras available/included differ by modality — drop prior selections so
        // Flexible add-ons (e.g. Monitor, Limpieza) are not billed again on Asistida.
        if (modalityChanged) {
            setSelectedExtras([]);
            setAvailableExtras([]);
        }

        setCurrentStep(BOOKING_STEPS.EXTRAS);
        setVisitedSteps(prev => new Set(prev).add(BOOKING_STEPS.EXTRAS));
    };

    const handleDateChange = (date: Date) => {
        setSelectedDate(date);
    };

    const handleTurnSelect = (turn: Turn) => {
        setSelectedTurn(turn);
        setCurrentStep(BOOKING_STEPS.MODALITY);
        setVisitedSteps(prev => new Set(prev).add(BOOKING_STEPS.MODALITY));
    };

    const onNext = () => {
        const nextStep = getNextStep(currentStep, hasMultipleRooms, includeSummaryStep);
        setCurrentStep(nextStep);
        setVisitedSteps(prev => new Set(prev).add(nextStep));
        if (nextStep === BOOKING_STEPS.TURN) {
            setShowCalendar(true);
        }
    };

    const onBack = () => {
        const prevStep = getPreviousStep(currentStep, hasMultipleRooms, landingMode, includeSummaryStep);
        if (prevStep !== null) {
            goToStep(prevStep);
        }
    };

    const handleBack = () => {
        if (currentStep === BOOKING_STEPS.TURN && !showCalendar) {
            setShowCalendar(true);
            return;
        }

        if (currentStep === BOOKING_STEPS.TURN && showCalendar) {
            const canGoBackFromTurn =
                getPreviousStep(BOOKING_STEPS.TURN, hasMultipleRooms, landingMode, includeSummaryStep) !== null;
            if (!canGoBackFromTurn) {
                return;
            }
        }

        onBack();
    };

    const onStepClick = (step: number) => {
        if (!visitedSteps.has(step)) return;
        if (!includeSummaryStep && step === BOOKING_STEPS.SUMMARY) return;
        if (!isStepNavigationAllowed(step, landingMode, showRoomInStepper)) return;
        goToStep(step);
    };

    const pay = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isPersonalDataFormValid(customerInfo, termsAccepted)) {
            return;
        }

        if (!selectedShop || !selectedRoom || !selectedDate || !selectedTurn || !selectedModality) {
            alert('Por favor, complete todos los campos requeridos antes de proceder al pago.');
            return;
        }

        if (!isDateBookable(selectedDate)) {
            alert('Las reservas para hoy no están disponibles. Por favor, elija una fecha a partir de mañana.');
            return;
        }

        const formattedDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

        const includedExtraIds = new Set(
            (selectedModality.includedExtras ?? []).map((extra) => Number(extra.extra_id))
        );
        const billableExtras = selectedExtras.filter(
            (extra) => !includedExtraIds.has(Number(extra.extra_id))
        );

        const checkout: Checkout = {
            shop_id: selectedShop.shop_id,
            room_id: selectedRoom.room_id,
            turn_id: Number(selectedTurn.turn_id),
            mode_id: selectedModality?.modalityId ?? 1,
            booking_date: formattedDate,
            extras: buildCheckoutExtras(selectedShop.shop_id, billableExtras, shopName),
            comments: customerInfo.additionalInfo,
            customer: {
                customer_name: customerInfo.name + ' ' + customerInfo.surname,
                customer_email: customerInfo.email,
                customer_phone: customerInfo.phone,
                customer_postal_code: customerInfo.postcode,
                gettoknow: customerInfo.gettoknow,
                terms: termsAccepted,
                news: marketingAccepted
            }
        };

        try {
            const token = await authenticate();
            if (!token) {
                alert('Failed to authenticate');
                return;
            }

            const url = `${config.API_BASE_URL}/clientapi/buy/step3payment`;
            await post(url, {
                data: checkout,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })
            .then((response: ApiResponse) => {
                const bookingId = response.message.bookingId;

                switch (Number(response.message.tpvType)) {
                    case 1:
                        console.log("REDSYS: " + bookingId)
                        callRedsys(response.message.tpvData);
                        break;

                    default:
                        alert('Tipo TPV desconocido');
                        break;
                }
            });

        } catch (error) {
            alert('Error during checkout: ' + error);
        }
    };

    const callRedsys = (data: any) => {
        const form = document.createElement('form');
        form.setAttribute('action', config.REDSYS_URL);
        form.setAttribute('method', 'POST');

        const field = document.createElement('input');
        field.setAttribute('type', 'hidden');
        field.setAttribute('name', 'Ds_SignatureVersion');
        field.setAttribute('value', data.Ds_SignatureVersion);
        form.appendChild(field);

        const field2 = document.createElement('input');
        field2.setAttribute('type', 'hidden');
        field2.setAttribute('name', 'Ds_MerchantParameters');
        field2.setAttribute('value', data.Ds_MerchantParameters);
        form.appendChild(field2);

        const field3 = document.createElement('input');
        field3.setAttribute('type', 'hidden');
        field3.setAttribute('name', 'Ds_Signature');
        field3.setAttribute('value', data.Ds_Signature);
        form.appendChild(field3);

        document.body.appendChild(form);
        form.submit();
    }

    const isPaymentRoute = location.pathname === '/pagoOk' || location.pathname === '/pagoErr';
    const isBookingStep = currentStep >= BOOKING_STEPS.SHOP && currentStep <= BOOKING_STEPS.PERSONAL_DATA;
    const isPaymentStep = currentStep === BOOKING_STEPS.PERSONAL_DATA;
    const isSummaryStep = currentStep === BOOKING_STEPS.SUMMARY;
    const isPaymentReady = isPersonalDataFormValid(customerInfo, termsAccepted);
    const showBottomBarPadding = selectedShop !== null && currentStep !== BOOKING_STEPS.ROOM;
    // Desktop: resumen sits beside the payment form. Mobile: already reviewed on step 6 — hide it here.
    const showResumenOnPaymentStep = isPaymentStep && !includeSummaryStep;
    const isSummaryVisible =
        showResumenOnPaymentStep || (!isSummaryStep && !isPaymentStep && isSummaryOpen);

    const includedExtraIds = new Set(
        (selectedModality?.includedExtras ?? []).map((extra) => Number(extra.extra_id))
    );
    const bookingTotalAmount = selectedModality
        ? Number(selectedModality.price ?? 0) +
          selectedExtras
              .filter((extra) => !includedExtraIds.has(Number(extra.extra_id)))
              .reduce((total, extra) => total + calculateExtraTotal(extra), 0)
        : null;

    useEffect(() => {
        if (showResumenOnPaymentStep) {
            setIsSummaryOpen(true);
        }
    }, [showResumenOnPaymentStep]);

    // Desktop skips the mobile-only summary step
    useEffect(() => {
        if (!includeSummaryStep && currentStep === BOOKING_STEPS.SUMMARY) {
            setCurrentStep(BOOKING_STEPS.PERSONAL_DATA);
            setVisitedSteps((prev) => new Set(prev).add(BOOKING_STEPS.PERSONAL_DATA));
            setIsSummaryOpen(true);
        }
    }, [includeSummaryStep, currentStep]);

    const handlePayFromBar = () => {
        const form = document.getElementById('booking-payment-form') as HTMLFormElement | null;
        form?.requestSubmit();
    };

    return (
        <div className="App">
            <SiteTopBar />
            <div className="App-content">
                {isPaymentRoute ? (
                    <Routes>
                        <Route path="/pagoOk" element={<PagoOk />} />
                        <Route path="/pagoErr" element={<PagoErr />} />
                    </Routes>
                ) : isBookingStep ? (
                    <div
                        className={[
                            'booking-flow',
                            showBottomBarPadding ? 'booking-flow--with-bottom-bar' : '',
                            isPaymentStep ? 'booking-flow--personal-data' : '',
                            isSummaryStep ? 'booking-flow--summary' : '',
                        ].filter(Boolean).join(' ')}
                    >
                        <Header
                            currentStep={currentStep}
                            visitedSteps={visitedSteps}
                            onStepClick={onStepClick}
                            landingMode={landingMode}
                            showRoomInStepper={showRoomInStepper}
                            includeSummaryStep={includeSummaryStep}
                        />
                        {currentStep !== BOOKING_STEPS.SUMMARY && (
                            <BookingStepIntro step={currentStep} />
                        )}
                        <div
                            className={`booking-flow-body ${isSummaryVisible ? 'booking-flow-body--summary-open' : ''}`}
                        >
                            <div className="booking-flow-main">
                                {currentStep === BOOKING_STEPS.SHOP && (
                                    <ShopSelectionStep
                                        shops={shops}
                                        selectedShop={selectedShop}
                                        onShopSelect={handleShopSelect}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.ROOM && selectedShop && rooms.length > 0 && (
                                    <RoomSelectionStep
                                        shop={selectedShop}
                                        rooms={rooms}
                                        selectedRoom={selectedRoom}
                                        onRoomSelect={handleRoomSelect}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.TURN && (
                                    <TurnSelectionStep
                                        shopId={selectedShop?.shop_id || 1}
                                        roomNum={selectedRoom?.room_num || 1}
                                        selectedDate={selectedDate}
                                        selectedTurn={selectedTurn}
                                        onDateSelect={handleDateChange}
                                        onTurnSelect={handleTurnSelect}
                                        showCalendar={showCalendar}
                                        setShowCalendar={setShowCalendar}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.MODALITY && (
                                    <ModalitySelectionStep
                                        shopId={selectedShop?.shop_id || 1}
                                        shopName={shopName}
                                        roomNum={selectedRoom?.room_num || 1}
                                        selectedDate={selectedDate}
                                        selectedTurn={selectedTurn}
                                        selectedModality={selectedModality?.id ?? null}
                                        onModalitySelect={handleModalitySelect}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.EXTRAS && (
                                    <ExtraSelectionStep
                                        shopId={selectedShop?.shop_id || 1}
                                        shopName={shopName}
                                        roomId={selectedRoom?.room_id || 1}
                                        selectedDate={selectedDate}
                                        selectedTurn={selectedTurn}
                                        selectedModality={selectedModality}
                                        selectedExtras={selectedExtras}
                                        onExtrasSelect={handleExtrasSelect}
                                        onAvailableExtrasChange={handleAvailableExtrasChange}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.SUMMARY && (
                                    <SummaryStep
                                        shopId={selectedShop?.shop_id || 1}
                                        shopName={shopName}
                                        room={selectedRoom}
                                        selectedModality={selectedModality}
                                        selectedDate={selectedDate}
                                        selectedTurn={selectedTurn}
                                        selectedExtras={selectedExtras}
                                        availableExtras={availableExtras}
                                        onRemoveExtra={handleRemoveExtra}
                                    />
                                )}
                                {currentStep === BOOKING_STEPS.PERSONAL_DATA && (
                                    <PersonalDataStep
                                        pay={pay}
                                        customerInfo={customerInfo}
                                        setCustomerInfo={setCustomerInfo}
                                        termsAccepted={termsAccepted}
                                        setTermsAccepted={setTermsAccepted}
                                        marketingAccepted={marketingAccepted}
                                        setMarketingAccepted={setMarketingAccepted}
                                    />
                                )}
                            </div>

                            <BookingSelectionPanel
                                isOpen={isSummaryVisible}
                                onClose={() => {
                                    if (!showResumenOnPaymentStep) {
                                        setIsSummaryOpen(false);
                                    }
                                }}
                                currentStep={currentStep}
                                shopId={selectedShop?.shop_id || 1}
                                shopName={shopName}
                                room={selectedRoom}
                                selectedDate={selectedDate}
                                selectedTurn={selectedTurn}
                                selectedModality={selectedModality}
                                selectedExtras={selectedExtras}
                                availableExtras={availableExtras}
                                onRemoveExtra={handleRemoveExtra}
                                dismissible={!showResumenOnPaymentStep}
                            />
                        </div>

                        <BookingBottomBar
                            currentStep={currentStep}
                            visitedSteps={visitedSteps}
                            landingMode={landingMode}
                            hasMultipleRooms={hasMultipleRooms}
                            showCalendar={showCalendar}
                            hasSelection={selectedShop !== null}
                            isPaymentReady={isPaymentReady}
                            isSummaryOpen={isSummaryVisible}
                            includeSummaryStep={includeSummaryStep}
                            totalAmount={bookingTotalAmount}
                            onSummaryOpenChange={setIsSummaryOpen}
                            onBack={handleBack}
                            onNext={onNext}
                            onPay={handlePayFromBar}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default App;
